import './polyfills.js'
import { getElementTag } from '@nrk/dh-server-libs/get-element-tag'
import { createTracker, Environment } from '@nrk/dh-snowplow'
import { EventSchema, ViewportEventKind } from '@nrk/snowplow-web'
import LevendeStillbilde from '@nrk/dh-basis-levende-stillbilde'
import StegvisVideo from '@nrk/dh-basis-stegvis-video'
import './lib/scroll-timeline.js'

import { HistorieScroll } from './components/app.ts'

import './components/vars.module.css'
import './components/app.module.css'
import './components/app.template-adjustments.module.css'
import './components/media/media.module.css'
import './components/media/media-types.module.css'
import './components/media/shared/object-fit.module.css'
import './components/media/shared/scroll-progress.module.css'
import './components/cards/cards.module.css'
import './components/cards/alt-text.module.css'

function safeDefine(elementName, constructor) {
  if (typeof customElements.get(elementName) === 'undefined') {
    customElements.define(elementName, constructor)
  }
}

const tag = getElementTag(__APP_NAME__, __APP_VERSION__)

class AppElement extends HTMLElement {
  name = tag
  #tracker
  #entryObserver
  #exitObserver

  connectedCallback() {
    this.#defineCustomElements()
    this.#createTracker()
    this.#startViewportLogger()
    this.addEventListener('dh-basis-scrollhistorie-trigger-activated', this.#handleTriggerActivated)
  }

  disconnectedCallback() {
    this.removeEventListener(
      'dh-basis-scrollhistorie-trigger-activated',
      this.#handleTriggerActivated,
    )
    this.#stopViewportLogger()
  }

  async #defineCustomElements() {
    // Register custom elements as needed
    safeDefine('dhhs-scrollhistorie', HistorieScroll)
    safeDefine('dhhs-levende-stillbilde', LevendeStillbilde)
    safeDefine('dhhs-stegvis-video', StegvisVideo)

    if (
      this.features.video &&
      (typeof customElements.get('dhhs-article-player') === 'undefined' ||
        typeof customElements.get('dhhs-video') === 'undefined')
    ) {
      const { AutoplayOnActivePlayerElement, Video } = await import(
        './components/media/video.ts' /* webpackChunkName: "dhhs-video" */
      )
      safeDefine('dhhs-article-player', AutoplayOnActivePlayerElement)
      safeDefine('dhhs-video', Video)
    }

    if (this.features.lottie && typeof customElements.get('dhhs-stegvis-lottie') === 'undefined') {
      const stegvisLottie = (
        await import('@nrk/dh-basis-stegvis-lottie' /* webpackChunkName: "dh-stegvis-lottie" */)
      ).default

      safeDefine('dhhs-stegvis-lottie', stegvisLottie)
    }

    if (this.features.kart && typeof customElements.get('dhhs-kart') === 'undefined') {
      const kart = (
        await import('./lib/karthistorie-chunk.js' /* webpackChunkName: "dh-karthistorie" */)
      ).default

      safeDefine('dhhs-kart', kart)
    }
  }

  #createTracker() {
    if (window.preventTracking) {
      return
    }

    if (process.env.DEV) {
      // @ts-ignore
      import('@nrk/dh-snowplow/mock').then(({ mock }) => mock()).catch(console.error)
    }

    this.#tracker = createTracker('dh-historiescroll', {
      environment: process.env.ENVIRONMENT as Environment,
      contentContext: {
        source: 'pp',
        id: this.dataset.selfId,
      },
    })
  }

  #handleTriggerActivated = (e) => {
    this.#tracker?.trackSelfDescribingEvent({
      schema: EventSchema.StepEvent,
      data: {
        current: e.detail.triggerIndex + 1,
        total: this.cards.length,
      },
    })
  }

  #startViewportLogger() {
    if (window.preventTracking) {
      return
    }

    this.#entryObserver = new IntersectionObserver((entries) => {
      if (!entries[0]?.isIntersecting) {
        return
      }
      this.#entryObserver?.disconnect()
      this.#exitObserver?.observe(this)
      this.#tracker?.trackSelfDescribingEvent({
        schema: EventSchema.ViewportEvent,
        data: {
          kind: ViewportEventKind.Entry,
        },
      })
    }, {})

    this.#exitObserver = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) {
          return
        }
        this.#exitObserver?.disconnect()
        this.#tracker?.trackSelfDescribingEvent({
          schema: EventSchema.ViewportEvent,
          data: {
            kind: ViewportEventKind.Exit,
          },
        })
      },
      { threshold: [1], rootMargin: '99000px 0px -100% 0px' },
    )

    this.#entryObserver.observe(this)
  }

  #stopViewportLogger() {
    this.#entryObserver?.disconnect()
    this.#exitObserver?.disconnect()
  }

  get features() {
    return JSON.parse(this.getAttribute('features'))
  }

  get cards() {
    return this.querySelectorAll('[data-show-background]')
  }
}

if (!customElements.get(tag)) {
  customElements.define(tag, AppElement)
}
